// ABOUTME: The Projectionist's player FSM — Playing | Holding | Dipping | Frozen (§2 of the Sequence Pilot review).
// ABOUTME: Dual-buffer surfaces, direct crossfade between chapters, move→hold segment engine,
// ABOUTME: frame-accurate segment boundaries (rVFC, timeupdate fallback), beat reporter.
//
// GRAMMAR (manifest-driven):
//   Every chapter is authored as a neutral-position sandwich (client decision 2026-07-16):
//     move  — forward motion, neutral → detail (start frame is the shared neutral pose)
//     hold  — the composed frame the chapter rests in; may be a single frozen frame
//             (start === end, the authored mid frame) or a loopable range
//     exit  — the rewind, detail → neutral, played ONLY when leaving the chapter
//   Because every chapter's first and last frames are the SAME neutral pose, the exit
//   rewind lands exactly where the next chapter's move begins — chapter changes read as
//   one continuous film (hero-page scroll), with the crossfade invisible over identical
//   frames. Revisits replay their move for the same reason: entering from neutral is
//   what makes the cut invisible.
//
// VERBS (from the backend, via useReelCues):
//   play_chapter · advance · hold · resume · home  (+ go_back, kept from Phase 0)

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { findSegmentIndex, resolveBeat, firstHoldIndex, likelyNextId, mediaUrl } from './reelSchema';
import { createStallGuard, backOffRate } from './stallGuard';
import { isDecoderWedge, kickDecoder } from './decoderKick';
import { supportsFrameCallbacks, startFramePump } from './frameBoundaryWatcher';

const PlayerState = {
  PLAYING: 'Playing',
  HOLDING: 'Holding',
  DIPPING: 'Dipping',   // transition in progress (crossfade preparing/running)
  FROZEN: 'Frozen',
};

const CROSSFADE_MS = 0;           // chapter changes are instant cuts (client 2026-07-16: the
                                  // crossfade's alpha dip read as a black blink; the shared
                                  // neutral frame makes a hard cut invisible anyway)
                                  // SPEC-19: beginEntry's onReady pauses the outgoing surface
                                  // BEFORE the opacity swap (not after a CROSSFADE_MS+80 delay)
                                  // on the assumption there is no fade left to hide motion under.
                                  // If this is ever set > 0 again, that pause needs to move back
                                  // behind the delay or the outgoing surface will freeze for the
                                  // whole fade instead of continuing to move under it.
const BOOT_FADE_MS = 500;         // cold-open fade from darkness
const FREEZE_EASE_MS = 400;       // rate-ease to zero on a hard stop (hold verb)
const SEG_EPSILON = 0.05;         // timeupdate granularity tolerance
// Segment boundaries are frame-accurate where requestVideoFrameCallback exists (see
// frameBoundaryWatcher.js): loop wraps since 2026-08-24, move/exit ends since 2026-08-27 —
// the 2× traversal review, where timeupdate's −SEG_EPSILON..+250ms sampling window meant up
// to 15 frames of the NEXT section bleeding through every cut, and freeze-holds landing off
// their authored frame. Each boundary fires when the LAST authored frame of the segment
// (endFrame − 1) is the one being presented, not when timeupdate happens to sample past it.
// While rVFC owns a boundary, timeupdate only takes it this far PAST the authored end — a
// backstop for the one case rVFC goes quiet while playback does not (a hidden tab: frames
// stop being composited but media time keeps running). Without rVFC (jsdom, older WebKit)
// timeupdate keeps full ownership at the usual −SEG_EPSILON tolerance, exactly as before.
const RVFC_WRAP_BACKSTOP_S = 0.25;
// timeupdate fires roughly every 250ms of real time. Ease the playback rate down
// over the final EASE_WINDOW_S of a move segment so the freeze-hold arrival is
// imperceptible (no visible seek-back snap when the boundary fires). The ramp — this
// constant and the floor pair below — governs ONLY the no-rVFC path since 2026-08-27
// (third pass on the dial jitter, same day as the floor raise): where the frame pump
// owns the boundary, the approach holds the traversal rate all the way to the
// frame-exact pin. See the gate in onTimeUpdate for why the ramp itself was the
// residual jitter.
const EASE_TO_HOLD_WINDOW_S = 1.5; // video-seconds before hold to start decelerating
const LOOP_RATE = 1;              // a breathing loop always plays at its authored speed
// NB there is deliberately NO cap on how long a departure waits for a loop's wrap point
// (2026-08-27, reversing the 0.4s MAX_LOOP_WAIT bound of 2026-08-17). The bound cut mid-cycle
// by pinning to the neutral end frame, and on screen that seek TELEPORTS the pose across the
// rest of the breath — "it doesn't let the loop finish, it jumps". Pose-continuity won over
// latency with eyes open: the cost is up to one authored cycle (idle breathes for 4 s) of the
// scene visibly completing its motion while the agent talks. The departure watchdog below is
// the strand-guard: a loop that stalls with a leave queued still gets forced through.
// Backstop for a departure queued behind a move. The ONLY thing that fires it is the move reaching
// its end, so anything that stops the move dead — a decode stall, a buffer underrun at high
// playback rate, a dropped timeupdate — silently swallows the guest's request. Observed live
// 2026-08-17: two consecutive requests for `movement` produced no picture change at all because
// the dial move never reported reaching its hold. Cause unconfirmed; this makes the symptom
// unreachable regardless of cause.
const DEPARTURE_GRACE_MS = 1200;
// beginEntry waits on `loadedmetadata` then `seeked` before it swaps surfaces, and neither is
// guaranteed to arrive: a cold seek into a large master can sit unresolved behind a slow range
// request. Both waits are unbounded, and the state they park in is DIPPING — where onTimeUpdate
// early-returns and every command queues into pendingTargetRef. So a seek that never completes
// is not a slow entry, it is a dead player. Proceed anyway after this long; a chapter entered on
// a frame that has not finished decoding self-corrects within a frame or two, whereas a hang does
// not self-correct at all. Generous enough not to misfire on a seek that is merely slow.
const ENTRY_SEEK_TIMEOUT_MS = 2500;
// Measured 2026-08-18: the timeout above used to `settle()` unconditionally, which calls
// onReady() -> play() even when readyState is still 0 (HAVE_NOTHING) — i.e. the browser has not
// received ANY data for this surface yet. That is not "proceeding on a slow seek", it is playing
// a video element with nothing decoded, which renders as a black frame that nothing ever
// recovers (stallGuard only reacts to `waiting`/`stalled`, neither of which fires from readyState
// 0). A bigger master file is more likely to still be at readyState 0 when a fixed timeout fires
// on a slow connection, which is why this got worse after a video swap without any JSON change.
// So: only settle for real on the timeout when there is at least SOME data (readyState >= 1) to
// show. At readyState 0, re-issue the load and give it a bounded number of extra windows first —
// still never hangs forever (the ORIGINAL P0 this timeout exists to fix), it just stops
// pretending a completely empty element is ready.
const ENTRY_LOAD_MAX_RETRIES = 2;
// The absolute ease floor is PERCEPTUAL, not just Chrome's playbackRate floor (~0.0625): below
// ~0.4× a 30 fps clip presents under 12 fps and the settle reads as the picture stepping to its
// hold in little jumps — the dial jitter was still faintly visible at 0.25× with a 1× traversal
// (team review 2026-08-27). The deep crawl used to buy boundary accuracy timeupdate couldn't
// give; onVideoFrame now lands the authored frame exactly, so a watchable tail costs nothing.
const EASE_TO_HOLD_MIN_RATE = 0.4;
// The ease floor is a RATIO of the traversal rate, not an absolute. Its job is to take the sting
// out of arriving at the freeze frame, and how fast is "too fast to arrive from" depends on how
// fast you were going. As an absolute 0.1 it was a 10:1 deceleration at 1× — a settle — and a
// 50:1 deceleration at 5×, which drops a 30fps clip to three frames a second and reads as the
// animation FREEZING short of its hold (reported 2026-08-17). Held as a ratio, the settle looks
// the same at any traversal rate. The ratio is 0.25, not 0.1, since 2026-08-27: even at 1× a
// 0.1 floor ran the last beat of the approach at 3 fps of a 30 fps clip, which reads as the
// picture JITTERING to its hold in small discrete jumps — the exact dial symptom from the team
// review. 0.25 keeps a 4:1 settle at any rate but never starves the frame cadence (15 fps at
// the 2× traversal rate), and the deep crawl no longer buys accuracy anyway — where rVFC
// exists, onVideoFrame lands the boundary on the authored frame regardless of arrival speed.
const EASE_TO_HOLD_FLOOR_RATIO = 0.25;
// The window is likewise capped as a FRACTION of the move it decelerates, not only as an
// absolute. 1.5s was authored against 5–16s raw moves (a 9–30% tail); the piecewise-retimed
// master compresses a traversal to 1.1–3.2s, so an absolute 1.5s swallowed short approaches
// whole — EXPLODED's entire 1.1s move sat inside the ramp and entered already decelerating
// (~0.76×), reading as the animation limping to its hold rather than arriving. Moves ≥ ~4.3s
// still get the full 1.5s window, so behaviour on long/raw masters is unchanged.
const EASE_TO_HOLD_MAX_FRACTION = 0.35;

const logPlayError = (err) => {
  if (err.name !== 'AbortError') console.error('[Projectionist] play failed:', err);
};

// Only chapters flagged `audio: true` (the intro film) play with sound — every other
// chapter is silent (the agent's voice comes over the WebSocket instead). Some browsers
// (notably older WebKit/Safari builds, and any UA that hasn't registered the "Enter
// Boutique" click as a user gesture yet) reject autoplay-with-sound with a `NotAllowedError`;
// falling back to a muted play keeps the film moving instead of leaving a frozen frame.
// The retry is scoped to NotAllowedError only (AbortError is the routine, expected
// rejection when a pause()/src swap lands before playback starts — logPlayError already
// ignores it, and retrying on it could resurrect a surface that was deliberately paused
// out from under this call, e.g. by a chapter change that landed mid-play()). The retry
// also re-checks that this element is still showing this chapter before touching it, so a
// late-arriving rejection from an abandoned play() can't restart audio on a frozen hold or
// a since-repurposed preload surface.
const playChapterVideo = (v, chapter) => {
  v.muted = !chapter.audio;
  v.dataset.chapterId = chapter.id;
  v.play().catch((err) => {
    logPlayError(err);
    if (err.name === 'NotAllowedError' && !v.muted && v.dataset.chapterId === chapter.id) {
      v.muted = true;
      v.play().catch(logPlayError);
    }
  });
};

/**
 * @param {[React.RefObject<HTMLVideoElement>, React.RefObject<HTMLVideoElement>]} videoRefs
 * @param {{
 *   manifest: object|null,
 *   onReelState?: (payload: object) => void,   // beat reporter → WebSocket
 *   onIntroFinished?: () => void,              // boot chapter completed or skipped
 *   traversalRateFor?: () => number|null,      // SPEC-86 D-9: per-move traversal rate, null = manifest's
 * }} opts
 */
function useVideoPlayer(videoRefs, { manifest, onReelState, onIntroFinished, traversalRateFor } = {}) {
  const [playerState, setPlayerState] = useState(PlayerState.DIPPING);
  const [duskActive, setDuskActive] = useState(true); // cold open only: born from darkness
  const [activeIdx, setActiveIdx] = useState(0);
  const [overlayInfo, setOverlayInfo] = useState({ chapterId: null, beat: null });
  // SPEC-26 D2: mirrors whichever of the 4 deferred-target refs below is currently set, so a
  // consumer (the point-tag layer) can suppress overlay content the instant a departure is
  // queued — 3 of the 4 defer branches (pendingTargetRef, loopExitTargetRef, moveExitTargetRef)
  // otherwise produce zero re-renders on their own, so polling playerState alone would miss them.
  const [pendingChapterId, setPendingChapterId] = useState(null);

  const stateRef = useRef(PlayerState.DIPPING);
  const activeIdxRef = useRef(0);
  const chapterRef = useRef(null);
  const segIdxRef = useRef(0);
  const visitedRef = useRef(new Set());
  const historyRef = useRef([]);
  const pendingTargetRef = useRef(null);    // redirect requested mid-transition (DIPPING) — always legal
  const exitTargetRef = useRef(null);       // next chapter, entered once the exit rewind reaches neutral
  const loopExitTargetRef = useRef(null);   // deferred target while waiting for the loop boundary
  const moveExitTargetRef = useRef(null);   // deferred target while move segment is still playing
  const enteringTargetRef = useRef(null);   // chapter beginEntry is landing right now — see syncPending
  const transitionToRef = useRef(null);     // stable ref to transitionTo (avoids circular dep in rAF stepper)
  const armDepartureWatchdogRef = useRef(null); // ditto: transitionTo arms it, it calls transitionTo
  const bootTimerRef = useRef(null);
  const fadeOutTimerRef = useRef(null);
  const freezeTimerRef = useRef(null);
  const departureTimerRef = useRef(null);
  const entrySeekTimerRef = useRef(null);
  const lastReportRef = useRef(null);
  const bootedRef = useRef(false);

  // Frame-accurate loop wrap (rVFC). `rvfcActiveRef` records whether at least one surface has a
  // live frame pump — it flips onTimeUpdate's loop boundary from detector to backstop. The
  // handler lives behind a ref (same pattern as transitionToRef) so the pump, started once at
  // mount, always calls the latest render's closure.
  const rvfcActiveRef = useRef(false);
  const onVideoFrameRef = useRef(null);

  // Stall recovery. The two actions live behind refs for the same reason transitionTo does: the
  // guard is constructed before the callbacks that implement them exist, and they close over
  // state that changes every render.
  const stallBackOffRef = useRef(null);
  const stallGiveUpRef = useRef(null);
  const stallGuardRef = useRef(null);
  if (!stallGuardRef.current) {
    stallGuardRef.current = createStallGuard({
      onBackOff: (n) => stallBackOffRef.current?.(n),
      onGiveUp: (n) => stallGiveUpRef.current?.(n),
    });
  }

  const byId = useMemo(
    () => (manifest ? Object.fromEntries(manifest.chapters.map((c) => [c.id, c])) : {}),
    [manifest],
  );

  // Frame cadence of the shared media file. The transform carries the spec's fps at the
  // manifest root; every encode we ship is authored and encoded at it (30/1, verified
  // 2026-08-24 against all three masters — frame k's timestamp is exactly k/fps).
  const fps = manifest?.fps ?? 30;

  // The wrap seek, shared by every path that wraps a loop (rVFC, timeupdate fallback, hidden-tab
  // backstop). Mid-frame target: frame k's timestamp is exactly k/fps, and a seek landing a hair
  // BELOW that boundary displays frame k−1 — aiming at the middle of the frame makes the landing
  // immune to float rounding in either direction. The authored integers ride on the manifest
  // (reelSpecTransform); hand-built manifests without them fall back to deriving from seconds.
  const wrapLoop = useCallback((v, seg) => {
    const startFrame = seg.loop.startFrame ?? Math.round(seg.loop.start * fps);
    v.currentTime = (startFrame + 0.5) / fps;
  }, [fps]);

  // Clip playback speed — authored per manifest (client wants brisk pacing on placeholders).
  //
  // This is the TRAVERSAL rate: it applies to `move` and `exit` segments, the camera getting from
  // one composed frame to another. Those are the only segments the guest is waiting through, and
  // the art spec authors them long — DIAL is frames 400→873, which is 15.8 REAL SECONDS at 1×.
  // The agent finishes its sentence long before the picture arrives (reported 2026-08-17).
  const playRate = manifest?.playbackRate ?? 1;

  // A LOOP hold is not traversal — it is the scene breathing while the guest thinks, and it is
  // meant to read as still. Running the idle loop (4s of media) at the traversal rate turns it
  // into a 0.8s strobe. So a loop always plays at its authored speed no matter how fast the
  // moves are, which is the whole reason the rate is per-segment rather than per-manifest.
  // Freeze-holds are paused outright, so their rate is only ever a value to restore.
  // `chapter.audio` marks a chapter that carries its own soundtrack — the intro film, and only
  // the intro film (useChapterManifest's makeIntroChapter). That is authored footage the guest is
  // meant to WATCH, not a camera getting from one composed frame to another, and it is a single
  // `move` segment, so the traversal rate caught it and played the whole opening at 5×
  // (reported 2026-08-17). Its own edit decides its pace; we never override it.
  const rateFor = useCallback(
    (seg, chapter) => {
      if (chapter?.audio) return LOOP_RATE;
      return seg && seg.type === 'hold' && seg.loop ? LOOP_RATE : (traversalRateFor?.() ?? playRate); // SPEC-86 D-9
    },
    [playRate, traversalRateFor],
  );

  const clearDepartureWatchdog = useCallback(() => {
    if (departureTimerRef.current) {
      clearTimeout(departureTimerRef.current);
      departureTimerRef.current = null;
    }
  }, []);

  const setStateBoth = useCallback((s) => {
    stateRef.current = s;
    setPlayerState(s);
  }, []);

  // SPEC-26 D2: recompute pendingChapterId from the 4 deferred-target refs' CURRENT values.
  // Called immediately after every assignment/clear of any of those refs (additive-only —
  // none of the refs' own read/write logic changes). Priority order doesn't matter in practice:
  // the refs are mutually exclusive in every reachable state (transitionTo only ever sets one
  // per call, and each clears any stale siblings first).
  //
  // An entry still in flight counts as pending too (2026-08-27). The 4 refs above all clear at
  // the hand-over — completeExit consumes exitTargetRef, transitionTo's no-exit fallthrough has
  // nothing queued by then — but beginEntry's `onReady` lands ASYNCHRONOUSLY (it waits on the
  // entry seek), and `overlayInfo.chapterId` only names the destination once it does. Between
  // those two moments React renders a window that says "nothing pending" while still reporting
  // the chapter we are LEAVING, and every overlay gated on that pair reappears over the
  // hand-over: the collection pill (name + price, top-centre) flashing back on the neutral
  // start frame mid-transition, reported 2026-08-27. A departure is not over until the
  // destination is on screen, so the entry itself holds the flag until `onReady` commits.
  const syncPending = useCallback(() => {
    const target = pendingTargetRef.current || exitTargetRef.current
      || loopExitTargetRef.current || moveExitTargetRef.current;
    if (target) {
      setPendingChapterId(target.chapter.id);
      return;
    }
    setPendingChapterId(enteringTargetRef.current ? enteringTargetRef.current.id : null);
  }, []);

  const activeVideo = useCallback(
    () => videoRefs[activeIdxRef.current]?.current ?? null,
    [videoRefs],
  );

  // ─── Beat reporter ──────────────────────────────────────────────────────────
  // Emits (story, segment, t, beat, variant, player_state) upstream whenever the
  // active beat / segment / story / state changes — the concierge's eyes.
  const report = useCallback(() => {
    const ch = chapterRef.current;
    const v = activeVideo();
    if (!ch || !v) return;
    const t = v.currentTime;
    const seg = ch.segments[segIdxRef.current];
    const beat = resolveBeat(ch, t);
    const key = `${ch.id}|${segIdxRef.current}|${beat}|${stateRef.current}`;
    if (key === lastReportRef.current) return;
    lastReportRef.current = key;

    setOverlayInfo({ chapterId: ch.id, beat });
    onReelState?.({
      story: ch.id,
      segment: seg ? `${segIdxRef.current}:${seg.type}` : null,
      t: Math.round(t * 100) / 100,
      beat,
      variant: ch.variant ?? 'shared',
      player_state: stateRef.current,
      // SPEC-61's mobile roll is its own chapter id (`collection_roll`), never renamed to
      // `collection` on the wire — additive only, so `story` itself is untouched for the
      // backend and every other existing consumer. Along for whoever needs to treat the two
      // as the same VIEW (found: SPEC-57's view toggle highlighting neither on mobile,
      // 2026-09-15 — see useReelViewToggle.js's observeReelState).
      rollChapter: manifest?.rollChapter ?? null,
    });
  }, [activeVideo, onReelState, manifest]);

  const clearFreezeEase = useCallback(() => {
    if (freezeTimerRef.current) {
      clearInterval(freezeTimerRef.current);
      freezeTimerRef.current = null;
    }
  }, []);

  // ─── Preloader ──────────────────────────────────────────────────────────────
  // The idle surface buffers the manifest's likely-next chapter so a crossfade
  // never waits on the network.
  const preloadNext = useCallback((chapter) => {
    if (!manifest) return;
    const nextId = likelyNextId(manifest, chapter);
    const next = nextId ? byId[nextId] : null;
    if (!next) return;
    const url = mediaUrl(manifest, next);
    const idle = videoRefs[1 - activeIdxRef.current]?.current;
    if (idle && (idle.getAttribute('src') || '') !== url) {
      idle.src = url;
      // 'metadata', not 'auto' — see index.js's <video> for why: an open-ended `bytes=0-` probe
      // (what 'auto' triggers) gets a 200 from this blob host instead of 206, which permanently
      // disables real seeking on that element. 'metadata' requests a small bounded range instead.
      idle.preload = 'metadata';
      idle.load();
    }
  }, [manifest, byId, videoRefs]);

  // ─── Entry (the incoming half of the crossfade) ─────────────────────────────
  // ALWAYS enters on the idle surface — even when the chapter lives in the same
  // media file — so every chapter change is a true two-surface crossfade.
  const beginEntry = useCallback((chapter, skipMove) => {
    if (!manifest) return;
    const url = mediaUrl(manifest, chapter);
    const prevActive = activeIdxRef.current;
    const surfaceIdx = 1 - prevActive;
    const v = videoRefs[surfaceIdx]?.current;
    if (!v) return;

    // The hand-over is not finished until `onReady` fires — keep the departure flagged until
    // then (see syncPending). Set after the guards above so an entry that never starts cannot
    // strand the flag. Re-entering (the redirect branch in onReady) simply overwrites it.
    enteringTargetRef.current = chapter;
    syncPending();

    if ((v.getAttribute('src') || '') !== url) {
      v.src = url;
      v.load();
    }
    // Stamp ownership at the moment this surface is claimed for `chapter`, independent of
    // whether it ever plays — this is what lets playChapterVideo's stale-rejection guard
    // detect a surface that's since been repurposed (or simply abandoned) rather than only
    // one that's been reused for another chapter that also happened to call playChapterVideo.
    v.dataset.chapterId = chapter.id;

    const entrySegIdx = skipMove ? firstHoldIndex(chapter) : 0;
    const seg = chapter.segments[entrySegIdx];
    const rawStart = skipMove && seg.loop ? seg.loop.start : seg.start;
    // bug fix: the mobile master is 29.97 fps (30000/1001, 3838 frames) while its spec declares 30
    // (3842), so a spec boundary seeked raw lands ~2 media frames early. On roll -> collection_approach
    // (spec frame 1618) that showed media frame 1616, the last CAROUSSEL close-up, for one frame (the
    // "watch blows up" flash). +1.5 spec frames always lands on or one frame past the new shot's
    // first media frame (floor((k+1.5)*0.999) >= ceil(k*0.999)) and stays inside SEG_EPSILON.
    // `rollChapter` is set on mobile manifests only, so every desktop seek stays the raw value.
    const startAt = manifest.rollChapter ? (Math.round(rawStart * fps) + 1.5) / fps : rawStart;

    // Any earlier entry still counting down is superseded by this one.
    if (entrySeekTimerRef.current) {
      clearTimeout(entrySeekTimerRef.current);
      entrySeekTimerRef.current = null;
    }

    const onReady = () => {
      stallGuardRef.current?.reset(); // fresh chapter, fresh ladder
      // A redirect arrived while we were preparing — honour the newest wish.
      if (pendingTargetRef.current) {
        const p = pendingTargetRef.current;
        pendingTargetRef.current = null;
        syncPending();
        beginEntry(p.chapter, p.skipMove);
        return;
      }

      // P6 (SPEC-19) belt-and-braces: pause the outgoing surface before the opacity
      // swap, not 80ms after. With CROSSFADE_MS=0 there is no fade left to hide a
      // still-playing outgoing surface under — the primary P6 fix (pause+pin in
      // transitionTo's no-exit fallthrough) already covers the case that was actually
      // bleeding, but no hard cut should ever swap away from a still-moving surface.
      videoRefs[prevActive]?.current?.pause();

      activeIdxRef.current = surfaceIdx;
      setActiveIdx(surfaceIdx); // triggers the CSS crossfade on both surfaces
      chapterRef.current = chapter;
      // The destination is on screen — the departure is over. Cleared here rather than in a
      // later effect so it commits in the SAME React batch as setActiveIdx/setStateBoth and the
      // report() below: overlays see chapterId, playerState and pendingChapterId flip together,
      // with no frame in between for the outgoing chapter's chrome to reappear in.
      enteringTargetRef.current = null;
      syncPending();
      segIdxRef.current = entrySegIdx;
      visitedRef.current.add(chapter.id);
      lastReportRef.current = null;

      v.playbackRate = rateFor(seg, chapter);
      // Entering directly at a loopless hold: pin the frame.
      const freezeHold = seg.type === 'hold' && !seg.loop;
      if (freezeHold) {
        v.muted = !chapter.audio; // dataset.chapterId already stamped above at src assignment
        v.pause();
        setStateBoth(PlayerState.HOLDING);
      } else {
        playChapterVideo(v, chapter);
        setStateBoth(seg.type === 'hold' ? PlayerState.HOLDING : PlayerState.PLAYING);
      }

      // The outgoing surface is already paused above; wait for it to be fully hidden
      // before the preloader touches its src (swapping src on a visible layer would
      // blank it).
      if (fadeOutTimerRef.current) clearTimeout(fadeOutTimerRef.current);
      fadeOutTimerRef.current = setTimeout(() => {
        fadeOutTimerRef.current = null;
        if (activeIdxRef.current !== prevActive) preloadNext(chapter);
      }, CROSSFADE_MS + 80);

      setDuskActive(false); // cold-open cover lifts on first entry; no-op afterwards
      report();
    };

    // Exactly one of: the seek lands, metadata never arrives, or the timeout fires. `settled`
    // makes the three mutually exclusive — a late `seeked` after a timed-out entry must not
    // re-run onReady against a surface that has since been claimed by another chapter.
    let settled = false;
    const settle = () => {
      if (settled) return;
      settled = true;
      if (entrySeekTimerRef.current) {
        clearTimeout(entrySeekTimerRef.current);
        entrySeekTimerRef.current = null;
      }
      onReady();
    };

    // Measured 2026-08-18: `seeked` used to go straight to `settle()` unconditionally. On a slow
    // connection into a large master, the browser can fire `seeked` while the seek was silently
    // clamped/ignored (the target byte range hadn't been fetched yet) — `currentTime` never
    // actually moved off wherever it was. Trusting that event unconditionally meant onReady()
    // called play() from the WRONG position, and since the FSM still expected THIS chapter's own
    // boundary (e.g. dial's freeze at 21.2s), the guest watched playback run forward through
    // every intervening chapter's footage (idle, then 360) until it happened to reach dial's
    // timestamp naturally — "freezes, then plays everything in order" instead of a direct cut.
    // Verify the seek actually landed before believing it; re-issue it a bounded number of times
    // if not. The entry timeout above is still the ultimate backstop if this never lands at all.
    let seekVerifyRetries = 0;
    const doSeek = () => {
      if (Math.abs(v.currentTime - startAt) < 0.01 && v.readyState >= 2) {
        settle();
        return;
      }
      const onSeeked = () => {
        if (Math.abs(v.currentTime - startAt) < SEG_EPSILON) {
          settle();
          return;
        }
        if (seekVerifyRetries < ENTRY_LOAD_MAX_RETRIES) {
          seekVerifyRetries += 1;
          console.warn(
            `[Projectionist] entry to "${chapter.id}" reported seeked at `
            + `${v.currentTime.toFixed(2)}s, not the requested ${startAt.toFixed(2)}s (retry `
            + `${seekVerifyRetries}/${ENTRY_LOAD_MAX_RETRIES}) — re-seeking rather than playing `
            + 'from the wrong position.',
          );
          v.addEventListener('seeked', onSeeked, { once: true });
          v.currentTime = startAt;
        } else {
          // Ladder exhausted — land wherever we can rather than hang forever; this is no worse
          // than the pre-fix behaviour, just no longer the FIRST thing tried.
          settle();
        }
      };
      v.addEventListener('seeked', onSeeked, { once: true });
      v.currentTime = startAt;
    };
    if (v.readyState >= 1) doSeek();
    else v.addEventListener('loadedmetadata', doSeek, { once: true });

    // Armed only if the entry is still pending — the fast path above has already settled.
    let loadRetries = 0;
    const armEntryTimeout = () => {
      entrySeekTimerRef.current = setTimeout(() => {
        entrySeekTimerRef.current = null;
        if (settled) return;
        if (v.readyState < 1 && loadRetries < ENTRY_LOAD_MAX_RETRIES) {
          loadRetries += 1;
          console.warn(
            `[Projectionist] entry to "${chapter.id}" has no data yet (readyState 0, retry `
            + `${loadRetries}/${ENTRY_LOAD_MAX_RETRIES}) — reloading instead of proceeding into `
            + 'a blank frame.',
          );
          v.load(); // re-issue the fetch — doSeek is already armed on 'loadedmetadata' above
          armEntryTimeout();
          return;
        }
        console.warn(
          `[Projectionist] entry to "${chapter.id}" never reported ready (readyState `
          + `${v.readyState}) — proceeding anyway rather than parking in Dipping forever.`,
        );
        settle();
      }, ENTRY_SEEK_TIMEOUT_MS);
    };
    if (!settled) armEntryTimeout();
  }, [manifest, fps, videoRefs, playRate, rateFor, preloadNext, report, setStateBoth, syncPending]);

  // ─── Exit completion ────────────────────────────────────────────────────────
  // The exit rewind has reached the neutral frame — enter the queued chapter now.
  // Its move begins on the identical neutral pose, so the switch is invisible.
  const completeExit = useCallback((v) => {
    const target = exitTargetRef.current;
    exitTargetRef.current = null;
    syncPending();
    v?.pause();
    if (!target) {
      // Defensive: no queued chapter — rest on the neutral frame rather than
      // playing on into the next section of a shared media file.
      setStateBoth(PlayerState.HOLDING);
      return;
    }
    setStateBoth(PlayerState.DIPPING);
    beginEntry(target.chapter, target.skipMove);
  }, [beginEntry, setStateBoth, syncPending]);

  // ─── Chapter transition ─────────────────────────────────────────────────────
  // Always legal, from any state, including mid-move: the guest is never locked in
  // a cutscene. From a hold, a chapter with an `exit` segment first plays its rewind
  // back to the neutral pose, THEN enters the next chapter — that continuity is the
  // whole seamlessness trick. Mid-move redirects crossfade directly (the fade masks
  // the frame mismatch).
  // SPEC-34: `skipExit` cuts AWAY from a chapter at rest WITHOUT playing its exit rewind —
  // the one new departure mode the carousel needs (collection@hold → variant slot: the hold
  // frame IS the lineup shot the slot starts on, so the cut is invisible and the exit
  // pull-back would be a detour). It rides every deferred-target payload below, because a
  // departure parked behind a move/loop must keep the mode it was asked with. Default false
  // → every pre-SPEC-34 path is byte-identical (AC-18).
  const transitionTo = useCallback((chapter, { skipMove = false, skipExit = false, _fromBoundary = false } = {}) => {
    if (!chapter) return;
    clearFreezeEase();
    // SPEC-19 P4 follow-up: every new request supersedes an earlier deferred leave that
    // never fired. Without this, a stale moveExitTargetRef/loopExitTargetRef left behind
    // by an abandoned request (e.g. advance() force-stepping past it, or this same call
    // taking the exit-segment/loop-defer/DIPPING-defer path below, none of which used to
    // clear it) could survive across several unrelated transitions and fire as a surprise
    // navigation once some later, unrelated chapter's hold-entry consumed it. The branches
    // below freshly re-set whichever ref is appropriate for THIS request.
    moveExitTargetRef.current = null;
    loopExitTargetRef.current = null;
    clearDepartureWatchdog();
    syncPending();
    if (stateRef.current === PlayerState.DIPPING) {
      pendingTargetRef.current = { chapter, skipMove, skipExit };
      syncPending();
      return;
    }
    // An exit rewind is already running — just retarget where it lands.
    if (exitTargetRef.current) {
      exitTargetRef.current = { chapter, skipMove, skipExit };
      syncPending();
      return;
    }
    const cur = chapterRef.current;
    const v = activeVideo();

    // Already here — do nothing at all. Every branch below is gated on `cur.id !== chapter.id`,
    // so without this guard a request for the chapter already on screen fell through to
    // beginEntry(), which claims the idle surface, seeks to segments[0].start and replays the
    // move. With CROSSFADE_MS = 0 that is an instant cut from the frame the guest is looking at
    // back to the first frame of its own animation — the "it jumps and suddenly resets" reported
    // 2026-08-17. Re-asking for where you already are is not a navigation; it is a no-op.
    //
    // The two exit refs were cleared just above, which is the right outcome here too: staying put
    // cancels a departure that was queued and never fired.
    if (cur && cur.id === chapter.id) return;

    // Mid-move: let the current move segment finish naturally before transitioning.
    // The move end frame is the same neutral pose as every other chapter's start/end,
    // so waiting costs at most a few seconds and guarantees a clean cut.
    if (cur && v && cur.id !== chapter.id && stateRef.current === PlayerState.PLAYING && !_fromBoundary) {
      moveExitTargetRef.current = { chapter, skipMove, skipExit };
      syncPending();
      armDepartureWatchdogRef.current?.(v, cur);
      return; // advanceSegmentOrEnd will pick this up when the move ends
    }

    // A chapter at rest departs through its authored `exit` rewind, which lands on the shared
    // neutral pose the next chapter's move begins from — that continuity IS the seamlessness.
    // FROZEN counts as at rest: a guest who said "wait" and then asked for somewhere else was
    // taking the no-exit hard-cut path below, because this test used to name HOLDING alone. The
    // rewind never played, so the cut happened from whatever mid-hold frame the freeze landed on
    // instead of from the neutral pose — the second half of the jumping reported 2026-08-17.
    // `clearFreezeEase()` at the top of this callback has already killed the ease interval, and
    // the exit branch re-asserts playbackRate, so a frozen surface is safe to drive from here.
    const atRest = stateRef.current === PlayerState.HOLDING
      || stateRef.current === PlayerState.FROZEN;
    // SPEC-34 skipExit: bypass the whole at-rest departure grammar — no loop-boundary
    // defer, no exit rewind, and crucially NOT the P6 neutral-frame pin below (seeking
    // the visible outgoing surface to its neutral frame before the cut would be exactly
    // the visible snap this mode exists to avoid — the resting frame IS the match point).
    // A frozen/holding freeze-hold is already paused on that frame; the fallthrough's
    // pause() covers a still-breathing loop, and beginEntry's onReady ordering
    // (pause-outgoing-before-swap) stays untouched either way.
    if (cur && v && cur.id !== chapter.id && atRest && !skipExit) {
      const holdSeg = cur.segments.find((s) => s.type === 'hold');
      // Loop hold: defer until the natural cycle boundary — ALWAYS (2026-08-27; see the note
      // where MAX_LOOP_WAIT_S used to live). A chapter with an `exit` needs the wrap anyway
      // (the rewind begins exactly at the loop's end); a no-exit loop needs it because the
      // only alternative is the pin below, whose seek teleports the pose across the rest of
      // the breath — the "doesn't let the loop finish, jumps" report. The wrap-fired
      // departure is pose-continuous by construction.
      // _fromBoundary prevents re-entering this defer when called by the boundary handler.
      // Only while HOLDING: a FROZEN surface is paused, so no boundary is coming and deferring
      // to one would strand the departure forever. The watchdog is the strand-guard for a
      // loop that stalls with a leave queued — its forced path is a seam, but a seam beats a
      // request the guest never saw honoured.
      if (holdSeg && holdSeg.loop && !_fromBoundary
          && stateRef.current === PlayerState.HOLDING) {
        loopExitTargetRef.current = { chapter, skipMove, skipExit };
        syncPending();
        armDepartureWatchdogRef.current?.(v, cur);
        return; // the wrap boundary (onVideoFrame / onTimeUpdate) fires the transition
      }

      const exitIdx = cur.segments.findIndex((s) => s.type === 'exit');
      if (exitIdx !== -1) {
        exitTargetRef.current = { chapter, skipMove, skipExit };
        syncPending();
        segIdxRef.current = exitIdx;
        const seg = cur.segments[exitIdx];
        // Pause before seeking so the outgoing surface is frozen during the cut.
        v.pause();
        if (Math.abs(v.currentTime - seg.start) > SEG_EPSILON) v.currentTime = seg.start;
        v.playbackRate = rateFor(seg, cur);
        playChapterVideo(v, cur);
        setStateBoth(PlayerState.PLAYING);
        report();
        return;
      }

      // P6 (SPEC-19): no exit segment (the whole-section loops idle/360) — the video
      // is still actively looping here even though the FSM is "Holding". Pause and pin
      // it to its neutral last frame before the hard cut, or the outgoing surface keeps
      // playing under the seek and bleeds a few frames of the next section's content.
      //
      // The pin aims at the MIDDLE of the neutral end frame, and its tolerance is half a
      // frame, not SEG_EPSILON — both halves of the "idle→chapter starts with a slight
      // jump" report (team review 2026-08-27). A departure deferred to the wrap arrives
      // with frame endFrame−1 presented — one breath-step short of the closing pose — and
      // SEG_EPSILON (1.5 frames) read that as "already pinned", so the cut jumped that
      // frame every time. And the raw boundary timestamp is exactly the float a seek may
      // land a hair below, displaying the frame BEFORE neutral (wrapLoop's mid-frame
      // rationale). The target frame is the loop-closure frame the gate in
      // scripts/verify_loop_closure.py certifies against the loop's start pose, and with
      // the encode's GOP at 30 it is a keyframe — the 1-frame corrective seek renders
      // fast enough to beat the incoming surface's own cold entry seek.
      v.pause();
      const neutralEnd = cur.segments[cur.segments.length - 1].end;
      const pinTarget = (Math.round(neutralEnd * fps) + 0.5) / fps;
      if (Math.abs(v.currentTime - pinTarget) > 0.6 / fps) v.currentTime = pinTarget;
    }
    // A boundary-fired redirect (deferred mid-move leave whose move ends into an exit/end,
    // the departure watchdog's forced path, the intro handoff) reaches here with the outgoing
    // surface still rolling — and DIPPING blinds onTimeUpdate, so nothing else stops it before
    // the entry seek settles. On a pre-sped retimed master those bled frames are 5× motion of
    // the next section flashing through the cut. Pause only — no corrective seek: a backward
    // reseek can itself render as a visible snap (the P6 caveat above).
    if (cur && v && !v.paused) v.pause();
    setStateBoth(PlayerState.DIPPING);
    beginEntry(chapter, skipMove);
  }, [activeVideo, playRate, rateFor, fps, beginEntry, clearFreezeEase, clearDepartureWatchdog,
      setStateBoth, report, syncPending]);

  // Keep the ref in sync so advanceSegmentOrEnd can call transitionTo without a circular dep.
  transitionToRef.current = transitionTo;

  // Arm the backstop for a deferred departure — parked behind a move (2026-08-17) or behind a
  // loop cycle's wrap (2026-08-27, when the wrap-defer became unconditional): wait the current
  // segment's own remaining runtime plus a grace period, and if the request is somehow STILL
  // queued after that, force it through. The ONLY thing that consumes the queue is the boundary
  // firing, so anything that stops playback dead — a decode stall, a buffer underrun, a dropped
  // timeupdate, a pump gone quiet — would otherwise silently swallow the guest's request. The
  // forced path cuts without the exit rewind / wrap alignment — a visible seam, but a seam
  // beats a request the guest made twice and never saw honoured.
  const armDepartureWatchdog = useCallback((v, ch) => {
    clearDepartureWatchdog();
    const seg = ch?.segments?.[segIdxRef.current];
    const rate = rateFor(seg, ch) || 1;
    const remainingMs = seg ? Math.max(0, ((seg.end - v.currentTime) / rate) * 1000) : 0;
    departureTimerRef.current = setTimeout(() => {
      departureTimerRef.current = null;
      const queued = moveExitTargetRef.current || loopExitTargetRef.current;
      if (!queued) return;  // the boundary landed and handed over normally, as it should
      console.warn(
        '[Projectionist] a deferred departure never reached its boundary — forcing '
        + `"${queued.chapter?.id}" through rather than dropping it.`,
      );
      moveExitTargetRef.current = null;
      loopExitTargetRef.current = null;
      syncPending();
      transitionToRef.current?.(queued.chapter, {
        skipMove: queued.skipMove, skipExit: queued.skipExit, _fromBoundary: true,
      });
    }, remainingMs + DEPARTURE_GRACE_MS);
  }, [clearDepartureWatchdog, rateFor, syncPending]);

  armDepartureWatchdogRef.current = armDepartureWatchdog;

  // ─── Chapter end ────────────────────────────────────────────────────────────
  const handleChapterEnd = useCallback((chapter) => {
    if (manifest?.boot && chapter.id === manifest.boot) {
      // Dedupe is downstream (App.js introFinishedSentRef + the backend's intro_prompt_sent),
      // so a silent boot chapter that already fired this at entry can safely fire it again.
      onIntroFinished?.();
      // Where the boot chapter hands off. Defaults to home (the intro film → idle path);
      // the returning-guest manifest sets bootHandoff === boot so the collection sweep
      // rests where it lands instead of being pulled back to the hero hold.
      const handoff = manifest.bootHandoff ?? manifest.home;
      if (manifest.boot !== handoff) {
        // Boot film finished → transition seamlessly to the handoff chapter (e.g. intro → idle).
        // _fromBoundary bypasses the mid-move defer: state is still PLAYING here, and
        // without it the intro→home transition would be queued into moveExitTargetRef
        // and never fire (the intro already ended), leaving the intro frame stuck.
        transitionTo(byId[handoff], { skipMove: false, _fromBoundary: true });
        return;
      }
      // boot === handoff: the boot chapter is where the guest stays.
      // Fire onIntroFinished but do not re-enter — rest at the neutral end frame.
    }
    activeVideo()?.pause();
    setStateBoth(PlayerState.HOLDING);
    report();
  }, [manifest, byId, onIntroFinished, transitionTo, activeVideo, setStateBoth, report]);

  // ─── Video error recovery ───────────────────────────────────────────────────
  // If the intro film's video surface errors (network / codec failure), onEnded never fires
  // and showIntroRef stays true forever (mic permanently suppressed, VAD dead). Release the
  // gate by treating the error the same as a natural chapter end.
  //
  // A genuine `error` (not `waiting`/`stalled` — stallGuard already owns that) on a REEL
  // chapter was previously a deliberate no-op here, on the assumption that it had no visible
  // effect on a detail chapter. Measured 2026-08-18: that assumption is false — a real decode
  // error blanks the active <video> element's rendered frame to black, and since `error` never
  // fires `waiting` first, stallGuard's ladder never arms and nothing ever recovers the guest.
  // Reusing stallGiveUpRef's landing logic (park on the segment's destination frame, unblock
  // the FSM) fixes that without navigating away like handleChapterEnd would.
  const onVideoError = useCallback((surfaceIdx) => {
    if (surfaceIdx !== activeIdxRef.current) return;
    const ch = chapterRef.current;
    if (!ch) return;
    if (manifest?.boot && ch.id === manifest.boot) {
      console.warn('[Projectionist] intro film load error — releasing VAD gate, skipping to home');
      handleChapterEnd(ch);
      return;
    }
    console.warn(`[Projectionist] video error on "${ch.id}" — landing its destination frame so the player cannot hang.`);
    stallGiveUpRef.current?.();
  }, [manifest, handleChapterEnd]);

  // ─── Segment engine ─────────────────────────────────────────────────────────
  const advanceSegmentOrEnd = useCallback((v, ch) => {
    const nextIdx = segIdxRef.current + 1;
    // The exit rewind never plays in natural flow — only transitionTo enters it.
    if (nextIdx >= ch.segments.length || ch.segments[nextIdx].type === 'exit') {
      // A transition was requested while the move was playing — the move just finished
      // at the neutral frame, so fire it now instead of resting at chapter end.
      // Exception: the boot FILM must always go through handleChapterEnd so onIntroFinished fires.
      // SPEC-43 (bug fix): the returning guest's silent boot sweep (collection, no audio) is not a
      // film — a `replay_intro` asked for mid-sweep used to be discarded here, leaving the film
      // gate raised over nothing. Film-ness (`audio`) decides, exactly as the skip button does.
      const deferred = moveExitTargetRef.current;
      const isBoot = manifest?.boot && ch.id === manifest.boot && ch.audio === true;
      if (deferred && !isBoot) {
        moveExitTargetRef.current = null;
        syncPending();
        transitionToRef.current?.(deferred.chapter, {
          skipMove: deferred.skipMove, skipExit: deferred.skipExit, _fromBoundary: true,
        });
        return;
      }
      if (deferred && isBoot) { moveExitTargetRef.current = null; syncPending(); } // clear it; handleChapterEnd will transition to home
      handleChapterEnd(ch);
      return;
    }
    segIdxRef.current = nextIdx;
    const ns = ch.segments[nextIdx];
    if (ns.type === 'hold') {
      // Reset after the ease-to-hold deceleration, to the rate THIS segment wants. A loop-hold
      // used to inherit whatever the move was running at — so with a fast traversal rate the
      // scene arrived and then kept breathing at 5×, which is the opposite of settling.
      v.playbackRate = rateFor(ns, ch);
      if (!ns.loop) {
        // Freeze-hold: pin the composed frame (a single-frame hold rests here
        // until the guest moves on — the exit rewind is the way out).
        v.pause();
        v.currentTime = ns.start;
      } else if (v.currentTime < ns.loop.start - SEG_EPSILON
                 || v.currentTime > ns.loop.end + SEG_EPSILON) {
        // A detached hold (360 resting in idle's borrowed breath): its loop frames live
        // elsewhere in the shared file, so entering it is a seek, not a fall-through. The
        // move's end frame and the loop's start frame are both the shared neutral pose,
        // so the cut is pose-matched — the same invariant every chapter seam rides on.
        // A contiguous loop (movement) lands inside its own range and never seeks here.
        wrapLoop(v, ns);
      }
      setStateBoth(PlayerState.HOLDING);
      // P4 (SPEC-19): a leave requested mid-move is stranded here unless routed now —
      // moveExitTargetRef is only otherwise consulted when a move ends into an exit/end
      // segment (above), never into a hold. Now that we're genuinely HOLDING, calling
      // transitionTo takes the correct path: a freeze-hold plays its exit immediately,
      // a loop-hold defers to the loop boundary then plays its exit.
      // Mirrors the sibling isBoot guard above: not reachable today (the intro chapter
      // is a single move segment with no hold), but if a future boot chapter ever
      // authors a hold, firing this would skip handleChapterEnd and onIntroFinished
      // would never fire — leaving the mic permanently suppressed for the session.
      const deferred = moveExitTargetRef.current;
      const isBootChapter = manifest?.boot && ch.id === manifest.boot && ch.audio === true; // SPEC-43: see sibling guard
      if (deferred && !isBootChapter) {
        moveExitTargetRef.current = null;
        syncPending();
        // A chapter WITH an exit routes normally: a freeze-hold plays its rewind at once, a
        // loop-hold defers to the wrap the rewind starts on. A chapter with NO exit (360's
        // borrowed breath) gets _fromBoundary instead: the move just landed the shared
        // neutral frame — the cleanest cut there will ever be — and without the flag the
        // now-unconditional wrap-defer would sit through a full breath cycle the guest
        // never asked to watch (they left mid-move; the scene was never at rest).
        const hasExitSeg = ch.segments.some((sg) => sg.type === 'exit');
        transitionToRef.current?.(deferred.chapter, {
          skipMove: deferred.skipMove, skipExit: deferred.skipExit, _fromBoundary: !hasExitSeg,
        });
      } else if (deferred && isBootChapter) {
        moveExitTargetRef.current = null; // never fire a leave out of the boot chapter's own hold
        syncPending();
      }
    } else {
      setStateBoth(PlayerState.PLAYING);
    }
  }, [manifest, handleChapterEnd, playRate, rateFor, setStateBoth, syncPending, wrapLoop]);

  // ─── Stall recovery ─────────────────────────────────────────────────────────
  // Rung 1..N: the element is starved, so stop asking it for so much. Easing the traversal rate
  // toward real time cuts the bytes-per-second it has to sustain proportionally, which is the
  // actual constraint — and re-issuing play() covers the case where it dropped out of playing
  // altogether. Deliberately silent to the guest: a move that finishes slightly slower than
  // authored is invisible, a frozen picture is not.
  stallBackOffRef.current = (attempt) => {
    const v = activeVideo();
    const ch = chapterRef.current;
    if (!v || !ch) return;
    const from = v.playbackRate;
    const to = backOffRate(from);
    console.warn(
      `[Projectionist] media starved on "${ch.id}" (attempt ${attempt}) — easing traversal `
      + `${from}x → ${to}x to let the buffer catch up.`,
    );
    // Data already buffered but the playhead is stuck: a wedged decoder, not a bandwidth stall (easing the rate cannot help).
    if (isDecoderWedge(v)) {
      console.warn(`[Projectionist] decoder wedged on "${ch.id}" (data buffered, playhead stuck) — nudging a half frame to flush it.`);
      kickDecoder(v, ch.segments[segIdxRef.current], fps);
    }
    v.playbackRate = to;
    if (v.paused) playChapterVideo(v, ch);
  };

  // Ladder exhausted. The picture is not coming back on its own, so stop waiting for a boundary
  // that will never fire and put the FSM where the boundary would have put it. The guest loses
  // the motion but lands on the composed frame — which is the frame the chapter is *about* — and
  // crucially the player accepts commands again.
  stallGiveUpRef.current = () => {
    const v = activeVideo();
    const ch = chapterRef.current;
    if (!v || !ch) return;
    const seg = ch.segments[segIdxRef.current];
    if (!seg) return;
    console.warn(
      `[Projectionist] media never recovered on "${ch.id}" (${seg.type} segment) — landing its `
      + 'destination frame so the player cannot hang.',
    );
    if (seg.type === 'hold' && seg.loop) {
      // Bug fix 2026-10-07: pausing a looping hold ended the hero idle for good (a first-timer on skip_intro_film cold-starts on it and a
      // slow first fetch outlasts the ladder). A buffering element resumes by itself the moment data arrives, so the pause was the
      // freeze; leave it running (re-issue play() in case it had been paused). The next `playing`/`timeupdate` resets the guard.
      // A WEDGED decoder (data buffered, playhead stuck) does not resume by itself: flush it with a half-frame seek, and keep the
      // ladder armed so it is nudged again every few seconds until frames flow (a new chapter or a `timeupdate` resets/disarms it).
      if (isDecoderWedge(v)) kickDecoder(v, seg, fps);
      playChapterVideo(v, ch);
      setStateBoth(PlayerState.HOLDING);
      report();
      if (stateRef.current !== PlayerState.FROZEN) {
        stallGuardRef.current?.reset();
        stallGuardRef.current?.notifyWaiting();
      }
      return;
    }
    v.pause();
    if (seg.type === 'hold') {
      // Nothing is blocked on a hold: a freeze-hold is already pinned, and a stalled loop has
      // merely stopped breathing. Forcing forward here would consume the chapter's exit and
      // navigate somewhere the guest never asked to go.
      if (!seg.loop && Math.abs(v.currentTime - seg.start) > SEG_EPSILON) v.currentTime = seg.start;
      setStateBoth(PlayerState.HOLDING);
      report();
      return;
    }
    if (Math.abs(v.currentTime - seg.end) > SEG_EPSILON) v.currentTime = seg.end;
    // Same two hand-offs onTimeUpdate would have made at the boundary, so a departure queued
    // behind this segment is honoured by the normal path rather than by the blunt watchdog.
    if (seg.type === 'exit') completeExit(v);
    else advanceSegmentOrEnd(v, ch);
  };

  // `waiting`/`stalled` from the active surface. FROZEN is excluded: the guest asked for a hold,
  // so a paused element there is the intended state, not a failure.
  const onWaiting = useCallback((surfaceIdx) => {
    if (surfaceIdx !== activeIdxRef.current) return;
    if (stateRef.current === PlayerState.FROZEN) return;
    stallGuardRef.current?.notifyWaiting();
  }, []);

  // `playing` from the active surface — frames are flowing again.
  const onPlaying = useCallback((surfaceIdx) => {
    if (surfaceIdx !== activeIdxRef.current) return;
    stallGuardRef.current?.notifyProgress();
  }, []);

  const onTimeUpdate = useCallback((surfaceIdx) => {
    if (surfaceIdx !== activeIdxRef.current) return;
    // Time moved on the active surface, which is the most direct evidence there is that the
    // element is healthy — stronger than `playing`, which only fires on a state transition.
    stallGuardRef.current?.notifyProgress();
    if (stateRef.current === PlayerState.DIPPING || stateRef.current === PlayerState.FROZEN) return;
    const v = videoRefs[surfaceIdx]?.current;
    const ch = chapterRef.current;
    if (!v || !ch) return;
    const seg = ch.segments[segIdxRef.current];
    if (!seg) return;
    const t = v.currentTime;

    if (seg.type === 'exit') {
      // Rewind reached the neutral frame — hand over to the queued chapter. With a frame
      // pump live, onVideoFrame owns this boundary at frame precision (same demotion
      // contract as the loop wrap below): timeupdate only backstops a quiet pump.
      const exitBoundary = rvfcActiveRef.current
        ? seg.end + RVFC_WRAP_BACKSTOP_S
        : seg.end - SEG_EPSILON;
      if (t >= exitBoundary) {
        completeExit(v);
        return;
      }
    } else if (seg.type === 'hold') {
      if (seg.loop) {
        const pending = loopExitTargetRef.current;
        // With a frame pump live, onVideoFrame owns this boundary at frame precision —
        // timeupdate only takes it well PAST the authored end, i.e. when presentation went
        // quiet while the media clock ran on (hidden tab). Without rVFC, this is the
        // detector, at its usual tolerance.
        const boundary = rvfcActiveRef.current
          ? seg.loop.end + RVFC_WRAP_BACKSTOP_S
          : seg.loop.end - SEG_EPSILON;
        if (t >= boundary) {
          if (pending) {
            // Loop completed its pass — fire the deferred transition now.
            loopExitTargetRef.current = null;
            syncPending();
            transitionTo(pending.chapter, {
              skipMove: pending.skipMove, skipExit: pending.skipExit, _fromBoundary: true,
            });
            return;
          }
          wrapLoop(v, seg); // seamless breath, no pending target
        }
      } else if (t >= seg.end - SEG_EPSILON && !v.paused) {
        v.pause(); // freeze-hold reached its boundary
      }
    } else if (t >= (rvfcActiveRef.current
        ? seg.end + RVFC_WRAP_BACKSTOP_S
        : seg.end - SEG_EPSILON)) {
      // Move boundary — demoted to a hidden-tab backstop while the pump owns it, exactly
      // like the exit above and the loop wrap: onVideoFrame lands the hold on the authored
      // frame instead of timeupdate's sampling lottery.
      advanceSegmentOrEnd(v, ch);
    } else if (!rvfcActiveRef.current) {
      // Ease playbackRate down as we approach a freeze-hold — timeupdate-owned boundaries
      // ONLY (2026-08-27, third pass on the dial jitter). Here the boundary fires up to
      // ~250ms of video time PAST seg.end and the pin then seeks visibly backward;
      // slowing the approach is what keeps that overshoot inside a frame or two. Under a
      // live frame pump there is nothing to mask — onVideoFrame lands endFrame − 1
      // exactly and the pin is a ≤1-frame forward seek — and the ramp itself was the
      // jitter that survived the 0.4×/0.25 floor raise: the 0.4× tail presents a 30 fps
      // master at 12 fps for its last ~half second of real time (real time is
      // 1/rate-weighted toward the floor), every non-integer rate on the way down sits on
      // an uneven vsync cadence, and the render already decelerates into its authored
      // rest pose, so the ramp double-eased exactly the section whose approach is a slow
      // fine-detail glide (dial). Full traversal rate to the pin is the fix — a softer
      // ramp only relocates the judder band.
      const nextSeg = ch.segments[segIdxRef.current + 1];
      // Ease only into freeze-holds (single-frame pins). Loop holds want full-speed
      // entry — easing then immediately re-ramping to playRate on advanceSegmentOrEnd
      // would cause a visible hitch at the move→hold boundary.
      if (nextSeg?.type === 'hold' && !nextSeg.loop) {
        const remaining = seg.end - t;
        const easeWindow = Math.min(
          EASE_TO_HOLD_WINDOW_S,
          (seg.end - seg.start) * EASE_TO_HOLD_MAX_FRACTION,
        );
        if (remaining > 0 && remaining <= easeWindow) {
          const frac = remaining / easeWindow; // 1.0 at window start → ~0 at boundary
          // P5 (SPEC-19): floored at EASE_TO_HOLD_MIN_RATE (0.4), so this never goes low
          // enough to hit Chrome's playbackRate floor (~0.0625) — the boundary handler's
          // pause()+currentTime=seg.end (advanceSegmentOrEnd) is what lands the hold frame.
          const from = rateFor(seg, ch);
          const floor = Math.max(EASE_TO_HOLD_MIN_RATE, from * EASE_TO_HOLD_FLOOR_RATIO);
          v.playbackRate = floor + (from - floor) * frac;
        }
      }
    }
    report();
  }, [videoRefs, advanceSegmentOrEnd, completeExit, rateFor, report, syncPending, wrapLoop]);

  // ─── Frame-accurate segment boundaries ──────────────────────────────────────
  // Fed by the rVFC pump (one per surface, armed at mount below). `metadata.mediaTime` is the
  // presentation timestamp of the frame being composited RIGHT NOW — ground truth timeupdate
  // cannot give. Every boundary fires when the last authored frame of the segment
  // (endFrame − 1) is on screen: deterministic to 0–1 frames, instead of timeupdate's
  // −1.5..+7.5 frame lottery (cut tails on early fires, bled exit/next-section frames on late
  // ones). Loop wraps moved here 2026-08-24; move/exit ends followed 2026-08-27 for the 2×
  // traversal rate, where the timeupdate lottery doubles in frames — a late move boundary
  // landed freeze-holds visibly past their authored frame, and a late exit boundary flashed
  // the next section's motion through the hand-over cut. onTimeUpdate keeps every one of
  // these boundaries as a hidden-tab backstop (+RVFC_WRAP_BACKSTOP_S) and remains the full
  // detector where rVFC is unsupported.
  const onVideoFrame = useCallback((surfaceIdx, metadata) => {
    if (surfaceIdx !== activeIdxRef.current) return;
    if (stateRef.current === PlayerState.DIPPING || stateRef.current === PlayerState.FROZEN) return;
    const v = videoRefs[surfaceIdx]?.current;
    const ch = chapterRef.current;
    if (!v || !ch) return;
    const seg = ch.segments[segIdxRef.current];
    if (!seg) return;
    if (v.seeking) return; // a boundary seek is already in flight — the post-seek frame re-arms this
    const frame = Math.round(metadata.mediaTime * fps);

    // Move/exit end — hand over exactly where the segment engine would, one frame early by
    // construction (endFrame − 1 is the last frame this segment owns). The intro film's
    // sentinel end (600 s) is unreachable, so the boot chapter still ends via `ended` only.
    if (seg.type === 'move' || seg.type === 'exit') {
      const segEndFrame = Math.round(seg.end * fps);
      if (frame < segEndFrame - 1) return;
      if (seg.type === 'exit') completeExit(v);
      else advanceSegmentOrEnd(v, ch);
      return;
    }

    if (seg.type !== 'hold' || !seg.loop) return;
    const endFrame = seg.loop.endFrame ?? Math.round(seg.loop.end * fps);
    if (frame < endFrame - 1) return;
    const pending = loopExitTargetRef.current;
    if (pending) {
      // Loop completed its pass — fire the deferred departure at the exact boundary. The
      // outgoing position is endFrame−1, within SEG_EPSILON of the exit's start, so the exit
      // branch skips its corrective back-seek and the rewind plays through without a snap.
      loopExitTargetRef.current = null;
      syncPending();
      transitionToRef.current?.(pending.chapter, {
        skipMove: pending.skipMove, skipExit: pending.skipExit, _fromBoundary: true,
      });
      return;
    }
    wrapLoop(v, seg);
  }, [videoRefs, fps, wrapLoop, syncPending, advanceSegmentOrEnd, completeExit]);
  onVideoFrameRef.current = onVideoFrame;

  // Natural end of the media file — same as crossing the last segment boundary.
  const onEnded = useCallback((surfaceIdx) => {
    if (surfaceIdx !== activeIdxRef.current) return;
    if (stateRef.current === PlayerState.DIPPING) return;
    const v = videoRefs[surfaceIdx]?.current;
    const ch = chapterRef.current;
    if (!v || !ch) return;
    // An exit whose end coincides with the file's end (e.g. collection at ~87.7s in V004) may
    // fire `ended` before a final timeupdate — complete the hand-over here.
    if (ch.segments[segIdxRef.current]?.type === 'exit' && exitTargetRef.current) {
      completeExit(v);
      return;
    }
    advanceSegmentOrEnd(v, ch);
  }, [videoRefs, advanceSegmentOrEnd, completeExit]);

  // ─── The five verbs ─────────────────────────────────────────────────────────

  const playChapter = useCallback((chapter, opts = {}) => {
    if (!chapter) return;
    // The approach/relight corridors (SPEC-34 dual-outro cut) are route hops, not places —
    // a go_back must return the guest to where they WERE, never into a transit strip.
    // SPEC-43: the film (`audio: true`) is transit too, not a place — after a `replay_intro`,
    // go_back must return to the chapter the guest was on BEFORE the film, not replay it.
    // SPEC-61 D-9: the mobile colour-roll joins the corridor set too — a go_back must never
    // hard-cut into frame 3553 from an arbitrary neutral pose (there is no authored travel
    // INTO the roll except the CAROUSSEL jump itself). Guarded by `manifest?.rollChapter`,
    // so this is inert on every desktop manifest (that key is never set there).
    const fromTransit = chapterRef.current
      && (chapterRef.current.id === manifest?.approachChapter
        || chapterRef.current.id === manifest?.relightChapter
        || chapterRef.current.audio === true
        || chapterRef.current.id === manifest?.rollChapter);
    if (chapterRef.current && chapterRef.current.id !== chapter.id && !fromTransit) {
      historyRef.current = [...historyRef.current, chapterRef.current.id];
    }
    // Revisits REPLAY their move (client decision 2026-07-16): every chapter enters
    // from the shared neutral pose, and that continuity is what makes the change
    // seamless — skipping the move was jumping straight to a detail frame.
    // SPEC-34: `enterAt: 'hold'` is the route-planner's name for the same landing goHome
    // has always used (skipMove → enter at the first hold); `skipExit` cuts away from a
    // rest without the exit rewind. Both default off — behaviour is byte-identical for
    // every existing caller (AC-18).
    const skipMove = opts.skipMove ?? (opts.enterAt === 'hold');
    const skipExit = opts.skipExit ?? false;
    transitionTo(chapter, { skipMove, skipExit });
  }, [transitionTo, manifest]);

  const advance = useCallback(() => {
    const v = activeVideo();
    const ch = chapterRef.current;
    if (!v || !ch || stateRef.current === PlayerState.DIPPING) return;
    const nextIdx = segIdxRef.current + 1;
    // The exit rewind is not an advancing beat — it only plays on the way out.
    if (nextIdx >= ch.segments.length || ch.segments[nextIdx].type === 'exit') {
      console.log('[Projectionist] advance: already at the last authored segment');
      return;
    }
    clearFreezeEase();
    segIdxRef.current = nextIdx;
    const seg = ch.segments[nextIdx];
    v.playbackRate = rateFor(seg, ch);
    if (Math.abs(v.currentTime - seg.start) > SEG_EPSILON) v.currentTime = seg.start;
    if (seg.type === 'hold' && !seg.loop) {
      v.muted = !ch.audio; // dataset.chapterId is already `ch.id` — same chapter, no src change
      v.pause();
      setStateBoth(PlayerState.HOLDING);
    } else {
      playChapterVideo(v, ch);
      setStateBoth(seg.type === 'hold' ? PlayerState.HOLDING : PlayerState.PLAYING);
    }
    if (seg.type === 'hold') {
      // SPEC-19 P4 follow-up: advance() force-steps past a move without going through
      // advanceSegmentOrEnd's own move→hold handoff, so a deferred leave queued while
      // that move was playing would otherwise be left stale — clear it rather than
      // strand it for an unrelated future chapter's hold-entry to fire unexpectedly.
      moveExitTargetRef.current = null;
      syncPending();
    }
    report();
  }, [activeVideo, playRate, rateFor, clearFreezeEase, setStateBoth, report, syncPending]);

  // Hard stop: rate-ease to zero over ~400ms — the scene holds its breath.
  const hold = useCallback(() => {
    const v = activeVideo();
    if (!v || stateRef.current === PlayerState.DIPPING || stateRef.current === PlayerState.FROZEN) return;
    clearFreezeEase();
    setStateBoth(PlayerState.FROZEN); // stops the loop-wrap logic immediately
    const stepMs = 50;
    const steps = FREEZE_EASE_MS / stepMs;
    const restRate = rateFor(chapterRef.current?.segments?.[segIdxRef.current], chapterRef.current);
    let rate = v.playbackRate || restRate;
    const decrement = rate / steps;
    freezeTimerRef.current = setInterval(() => {
      rate -= decrement;
      if (rate <= 0.15 || v.paused) {
        clearFreezeEase();
        v.pause();
        v.playbackRate = restRate;
        report();
      } else {
        v.playbackRate = rate;
      }
    }, stepMs);
  }, [activeVideo, playRate, rateFor, clearFreezeEase, setStateBoth, report]);

  const resume = useCallback(() => {
    const v = activeVideo();
    const ch = chapterRef.current;
    if (!v || !ch || stateRef.current !== PlayerState.FROZEN) return;
    clearFreezeEase();
    let idx = Math.max(0, findSegmentIndex(ch, v.currentTime));
    // A freeze-hold and its exit share a start frame — when frozen on that frame
    // with no departure queued, rest in the hold; never play the rewind by accident.
    if (ch.segments[idx]?.type === 'exit' && !exitTargetRef.current && idx > 0
        && Math.abs(v.currentTime - ch.segments[idx].start) <= SEG_EPSILON) {
      idx -= 1;
    }
    segIdxRef.current = idx;
    const seg = ch.segments[idx];
    v.playbackRate = rateFor(seg, ch); // resolved AFTER the segment is known — a loop resumes at 1x
    if (seg.type === 'hold' && !seg.loop && v.currentTime >= seg.end - SEG_EPSILON) {
      v.muted = !ch.audio; // dataset.chapterId is already `ch.id` — same chapter, no src change
      setStateBoth(PlayerState.HOLDING); // frozen on a terminal frame — nothing to play
    } else {
      playChapterVideo(v, ch);
      setStateBoth(seg.type === 'hold' ? PlayerState.HOLDING : PlayerState.PLAYING);
    }
    report();
  }, [activeVideo, playRate, rateFor, clearFreezeEase, setStateBoth, report]);

  const goHome = useCallback(() => {
    if (!manifest) return;
    playChapter(byId[manifest.home], { skipMove: true });
  }, [manifest, byId, playChapter]);

  const goBack = useCallback(() => {
    const history = historyRef.current;
    if (history.length === 0) return;
    const prevId = history[history.length - 1];
    historyRef.current = history.slice(0, -1);
    const prev = byId[prevId];
    if (prev) transitionTo(prev, { skipMove: false }); // replay the move from the shared neutral frame
  }, [byId, transitionTo]);

  // Skip affordance on the intro film: lands exactly where the film would have.
  // _fromBoundary bypasses the mid-move defer (intro is still PLAYING on tap).
  // SPEC-43: act when the current chapter is the FILM (`audio: true`), not only when it is boot.
  // A replayed film (`replay_intro`) is PLAYING and mid-move on a manifest whose boot is the
  // collection sweep; this is the only PLAYING-safe escape (_fromBoundary), and refusing off the
  // boot chapter left a replay with no way out.
  const skipIntro = useCallback(() => {
    const cur = chapterRef.current;
    if (!manifest?.boot || !cur || (cur.id !== manifest.boot && cur.audio !== true)) return;
    onIntroFinished?.();
    transitionTo(byId[manifest.home], { skipMove: false, _fromBoundary: true });
  }, [manifest, byId, onIntroFinished, transitionTo]);

  // ─── Boot: cold open — darkness, then the intro film (or the arrival hold) ──
  useEffect(() => {
    if (!manifest || bootedRef.current) return;
    bootedRef.current = true;
    const boot = manifest.boot ? byId[manifest.boot] : null;
    // NB the returning guest's welcome does NOT wait on this chapter ending: App.js's WS onopen
    // already fires `intro_finished` 500ms after connect when the intro gate is down, so the
    // "welcome back" plays OVER the collection sweep rather than after it. The onIntroFinished
    // that handleChapterEnd fires when the sweep lands is a deduped no-op by then.
    // stateRef is DIPPING at mount; darkness cover lifts immediately on entry, no delay.
    // If there's a boot film, it plays from darkness; if not, go straight to home.
    bootTimerRef.current = setTimeout(() => {
      beginEntry(boot ?? byId[manifest.home], false);
    }, 0); // no BOOT_FADE_MS delay — fade effect is driven by duskActive CSS transition
  }, [manifest, byId, beginEntry]);

  // ─── Frame pumps: one per surface, for the rVFC loop wrap above ─────────────
  // Armed once at mount; rVFC callbacks are per-element and survive src swaps, and a paused
  // element presents nothing, so an idle pump costs nothing. The refs themselves are stable
  // (useRef in the component), so this runs exactly once. Where the browser lacks rVFC
  // (jsdom, older WebKit) the flag stays false and onTimeUpdate keeps full ownership of loop
  // wraps — behaviour is then identical to the pre-rVFC player.
  const surfaceRefA = videoRefs[0];
  const surfaceRefB = videoRefs[1];
  useEffect(() => {
    const stops = [surfaceRefA, surfaceRefB].map((ref, idx) => {
      const v = ref?.current;
      if (!supportsFrameCallbacks(v)) return null;
      return startFramePump(v, (now, metadata) => onVideoFrameRef.current?.(idx, metadata));
    });
    rvfcActiveRef.current = stops.some(Boolean);
    return () => {
      stops.forEach((stop) => stop?.());
      rvfcActiveRef.current = false;
    };
  }, [surfaceRefA, surfaceRefB]);

  // Cleanup timers on unmount.
  useEffect(() => () => {
    if (bootTimerRef.current) clearTimeout(bootTimerRef.current);
    if (fadeOutTimerRef.current) clearTimeout(fadeOutTimerRef.current);
    if (entrySeekTimerRef.current) clearTimeout(entrySeekTimerRef.current);
    if (departureTimerRef.current) clearTimeout(departureTimerRef.current);
    stallGuardRef.current?.reset();
    clearFreezeEase();
  }, [clearFreezeEase]);

  return {
    playerState,
    duskActive,
    activeIdx,
    overlayInfo,
    pendingChapterId,
    playChapter,
    advance,
    hold,
    resume,
    goHome,
    goBack,
    skipIntro,
    onTimeUpdate,
    onEnded,
    onVideoError,
    onWaiting,
    onPlaying,
  };
}

export { PlayerState, CROSSFADE_MS, BOOT_FADE_MS };
export default useVideoPlayer;
